// Feb 2026 · Non-technical funnel debug page. Operator types the
// conversation name in a search box, sees exactly which status the
// funnel will show for it + every underlying row. Replaces the need
// for DevTools console commands.
import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, Search, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FunnelDebugPage() {
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const run = async (e) => {
    e?.preventDefault?.();
    if (!q.trim() || q.trim().length < 3) {
      setErr('Type at least 3 characters (e.g. "Encuragen" or "Faes").');
      return;
    }
    setLoading(true);
    setErr(null);
    setData(null);
    try {
      const r = await axios.get(
        `${API}/api/dashboard/funnel-debug?q=${encodeURIComponent(q.trim())}`,
        { withCredentials: true },
      );
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="funnel-debug-page">
      <div className="max-w-5xl mx-auto px-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Search className="w-6 h-6 text-indigo-600" />
            Funnel Debug
          </h1>
          <div className="text-sm text-slate-500 mt-1">
            Type any part of an asset / biotech / pharma name to see what status the BD funnel will show for that conversation, plus every underlying outreach row.
          </div>
        </div>

        <form onSubmit={run} className="mb-6 flex gap-2">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. Encuragen · Faes · Xavier · Sunhawk · Alcon"
            className="flex-1 px-4 py-2 border border-slate-300 rounded bg-white focus:outline-none focus:border-indigo-500 text-sm"
            data-testid="funnel-debug-input"
            autoFocus
          />
          <button
            type="submit"
            disabled={loading || q.trim().length < 3}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded disabled:opacity-50"
            data-testid="funnel-debug-search"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Search
          </button>
        </form>

        {err && (
          <div className="bg-rose-50 border border-rose-200 rounded p-3 text-rose-800 text-sm mb-4" data-testid="funnel-debug-error">
            {err}
          </div>
        )}

        {data && (
          <div className="space-y-4" data-testid="funnel-debug-results">
            {data.conversations.length === 0 && (
              <div className="bg-white border border-slate-200 rounded p-6 text-center text-slate-500">
                No outreach rows matched <code>&quot;{data.query}&quot;</code>. Try a shorter or different substring.
              </div>
            )}
            {data.conversations.map((c, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded shadow-sm" data-testid={`funnel-debug-conv-${i}`}>
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 rounded-t">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <div className="text-xs text-slate-500">Conversation:</div>
                    <div className="font-semibold text-slate-800">
                      {c.asset || <em className="text-slate-400">no asset</em>}
                    </div>
                    <span className="text-slate-400">·</span>
                    <div className="text-slate-700">{c.biotech_name || c.biotech || <em className="text-slate-400">no biotech</em>}</div>
                    <span className="text-slate-400">·</span>
                    <div className="text-slate-700 capitalize">{c.pharma || <em className="text-slate-400">no pharma</em>}</div>
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Funnel will show:</span>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded text-xs font-bold" data-testid={`funnel-debug-conv-${i}-verdict`}>
                      {c.funnel_will_show || <em>(none — hidden)</em>}
                    </span>
                    <span className="text-[10px] text-slate-500 ml-2">
                      {c.row_count} row{c.row_count === 1 ? '' : 's'} · distinct: {c.distinct_statuses.join(' / ') || '—'}
                    </span>
                  </div>
                </div>
                <div className="p-3">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Rows (latest first)</div>
                  <table className="w-full text-xs" data-testid={`funnel-debug-conv-${i}-rows`}>
                    <thead className="text-slate-500">
                      <tr>
                        <th className="text-left px-2 py-1 font-normal">Contact</th>
                        <th className="text-left px-2 py-1 font-normal">Status</th>
                        <th className="text-left px-2 py-1 font-normal">Last Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.rows.map((r, j) => (
                        <tr key={r.id || j} className={`border-t border-slate-100 ${j === 0 ? 'bg-emerald-50/40' : ''}`}>
                          <td className="px-2 py-1.5 text-slate-700">{r.contact || <em className="text-slate-400">—</em>}</td>
                          <td className="px-2 py-1.5 font-medium text-slate-900">
                            {j === 0 && <CheckCircle2 className="w-3 h-3 text-emerald-600 inline mr-1" />}
                            {r.status}
                          </td>
                          <td className="px-2 py-1.5 text-slate-500 font-mono text-[11px]">{(r.updated_at || '').replace('T', ' ').slice(0, 19)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {c.rows.length > 1 && c.distinct_statuses.length > 1 && (
                    <div className="mt-2 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800 flex items-start gap-1.5">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                      <div>
                        Multiple statuses in this conversation. The funnel shows the row highlighted above (latest by updated_at). If that&apos;s wrong, either the older row has a stale/wrong updated_at, or you updated a different row than expected.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
